import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import { Bibliography, Tirage, TirageInBibliography } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import TirageInBibliographyDrawer from "./drawer/TirageInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage | undefined;
  bibliography: Bibliography | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<TirageInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionTitre",
    header: "bibliographieDescriptionTitre",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAuteur",
    header: "bibliographieDescriptionAuteur",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

const TirageInBibliographyLinks = ({
  id,
  tirage,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "tirageInBibliography",
    type,
  );

  const { mutate: deleteTirageInBibliography } = useDeleteComplexLink(
    id,
    "tirageInBibliography",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteTirageInBibliography({
        id,
        relation: "tirageInBibliography",
      });
    },
    [deleteTirageInBibliography],
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const columns: ColumnDef<TirageInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : []),
      {
        accessorKey: "tirageDansBibliographieDescriptionReproduitPage",
        header: t("tirageDansBibliographieDescriptionReproduitPage"),
        size: 50,
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionCitePage",
        header: t("tirageDansBibliographieDescriptionCitePage"),
        size: 50,
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionReproductionCouleur",
        header: t("tirageDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionNonPagine",
        header: t("tirageDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "tirageDansBibliographieNotes",
        header: t("tirageDansBibliographieNotes"),
        cell: (props: any) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: (props: any) => {
          const data = props.row.original as TirageInBibliography;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <TirageInBibliographyDrawer
                key={props.row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "bibliography" && (
                <ShowElements
                  type="tirage"
                  id={props.row.original.tirage.id}
                  elements={[
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value:
                        props.row.original.tirage
                          .tirageDescriptionNumeroEnregistrement ?? "",
                      type: "text",
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value:
                        props.row.original.tirage.tirageDescriptionNotes ?? "",
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.tirage.attachments ?? [],
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "tirage" && (
                <ShowElements
                  type="bibliography"
                  id={props.row.original.bibliography.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "tirage") {
      return (
        <TirageToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <BibliographyToTirageLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    tirage,
    bibliography,
    columns,
    fields,
    id,
    isAddMode,
    relatedData,
    searchContent,
    type,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type !== "tirage" ? t("tiragesRelated") : t("bibliographiesRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

type TirageToBibliographyLinksProps = LinksTableProps<TirageInBibliography> & {
  tirage: Tirage | undefined;
};

type BibliographyToTirageLinksProps = LinksTableProps<TirageInBibliography> & {
  bibliography: Bibliography | undefined;
};

const TirageToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  tirage,
  isAddMode,
  columnsLink,
  searchContent,
}: TirageToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resourceId: id,
      resultType: "bibliography",
      searchQuery: searchContent,
      pagination: getPagination,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("bibliographieDescriptionTitre"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Bibliography;
          if (!tirage) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="bibliography"
                id={row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      row.original.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <TirageInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={tirage.id}
                type="tirage"
                data={{
                  id: "",
                  bibliography: data,
                  tirage: tirage,
                  tirageDansBibliographieDescriptionReproduitPage: "",
                  tirageDansBibliographieDescriptionCitePage: "",
                  tirageDansBibliographieDescriptionPhotographe: "",
                  tirageDansBibliographieDescriptionReproductionCouleur: false,
                  tirageDansBibliographieDescriptionNonPagine: false,
                  tirageDansBibliographieNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [tirage, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsBibliographies}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedBibliographies?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsBibliographies,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedBibliographies?.data,
      notRelatedBibliographies?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

const BibliographyToTirageLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  searchContent,
}: BibliographyToTirageLinksProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resourceId: id,
    resultType: "tirage",
    searchQuery: searchContent,
    pagination: getPagination,
  });

  const { t } = useTranslation();

  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.tirageDescriptionNumeroEnregistrement}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Tirage> }) => {
          const data = row.original as Tirage;
          if (!bibliography) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="tirage"
                id={row.original.id}
                elements={[
                  {
                    label: t("tirageDescriptionNumeroEnregistrement"),
                    value:
                      row.original.tirageDescriptionNumeroEnregistrement ?? "",
                    type: "text",
                  },
                  {
                    label: t("tirageDescriptionNotes"),
                    value: row.original.tirageDescriptionNotes ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments ?? [],
                    type: "image",
                  },
                ]}
              />
              <TirageInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={bibliography.id}
                type="bibliography"
                data={{
                  id: "",
                  bibliography: bibliography,
                  tirage: data,
                  tirageDansBibliographieDescriptionReproduitPage: "",
                  tirageDansBibliographieDescriptionCitePage: "",
                  tirageDansBibliographieDescriptionPhotographe: "",
                  tirageDansBibliographieDescriptionReproductionCouleur: false,
                  tirageDansBibliographieDescriptionNonPagine: false,
                  tirageDansBibliographieNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [bibliography, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsTirages}
          data={notAlreadyRelatedTirages?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelatedTirages?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsTirages,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelatedTirages?.data,
      notAlreadyRelatedTirages?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

export default TirageInBibliographyLinks;
